// Utilities used by the global message-notification hook to put a
// notification in front of the user without leaving the web app:
//
//   * setUnreadInTitle(n)      — prepend "(n) " to the tab title
//   * playMessageChime()       — short soft tone via Web Audio API
//   * fireBrowserNotification  — Notification-API-based desktop popup

import { getSoundEnabled, getBrowserNotificationsEnabled, isNotificationApiSupported } from './notification-prefs';

// --- tab title ---------------------------------------------------------------

const COUNT_PREFIX = /^\(\d+(\+?)\)\s+/;

// Recompute the baseline from the CURRENT document.title on every call.
// A memoized baseline captured once at mount goes stale on route changes
// (e.g. React Helmet updates the title when entering /messages/:id) and
// resets the tab to the original landing title instead of the route's.
const stripExistingPrefix = (title: string): string => title.replace(COUNT_PREFIX, '');

export const setUnreadInTitle = (count: number): void => {
  if (typeof document === 'undefined') return;
  const base = stripExistingPrefix(document.title);
  document.title = count > 0 ? `(${count > 99 ? '99+' : count}) ${base}` : base;
};

export const resetTitle = (): void => setUnreadInTitle(0);

// --- chime ------------------------------------------------------------------
//
// Vanessa's report: notifications didn't fire when the messages tab was open
// in the BACKGROUND of her browser (she was on a different tab). Two pieces
// were broken: useGlobalMessageNotifications was suppressing the chime when
// the user was on the focused conversation page even if the tab itself was
// hidden, AND modern Chrome suspends AudioContext for tabs that have been
// hidden a while — so even if we'd fired the chime, it would have been silent.
//
// We now render the chime once via OfflineAudioContext into a WAV blob and
// replay it through HTMLAudioElement. Audio elements don't suspend the way
// AudioContext does — once the user has interacted with the page (any click)
// the browser's autoplay policy lets us play() from a background tab.

const SAMPLE_RATE = 44100;
const DURATION_S = 0.35;

let cachedAudio: HTMLAudioElement | null = null;
let renderInFlight: Promise<HTMLAudioElement | null> | null = null;

const audioBufferToWavBlob = (buffer: AudioBuffer): Blob => {
  const numChannels = 1;
  const samples = buffer.getChannelData(0);
  const dataLength = samples.length * 2;
  const ab = new ArrayBuffer(44 + dataLength);
  const view = new DataView(ab);
  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);          // PCM chunk size
  view.setUint16(20, 1, true);           // format = PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, buffer.sampleRate, true);
  view.setUint32(28, buffer.sampleRate * numChannels * 2, true); // byte rate
  view.setUint16(32, numChannels * 2, true);                     // block align
  view.setUint16(34, 16, true);                                  // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, dataLength, true);
  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    offset += 2;
  }
  return new Blob([ab], { type: 'audio/wav' });
};

const renderChimeAudio = async (): Promise<HTMLAudioElement | null> => {
  if (typeof window === 'undefined') return null;
  const Ctor =
    (window as unknown as { OfflineAudioContext?: typeof OfflineAudioContext }).OfflineAudioContext ??
    (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
  if (!Ctor) return null;
  try {
    const offline = new Ctor(1, Math.ceil(SAMPLE_RATE * DURATION_S), SAMPLE_RATE);
    const o1 = offline.createOscillator();
    const o2 = offline.createOscillator();
    const g = offline.createGain();
    o1.type = 'sine';
    o2.type = 'sine';
    o1.frequency.setValueAtTime(880, 0);             // A5
    o2.frequency.setValueAtTime(1175, 0);            // D6
    o2.frequency.setValueAtTime(1320, 0.12);         // E6 — small triadic lift
    g.gain.setValueAtTime(0.0001, 0);
    g.gain.exponentialRampToValueAtTime(0.14, 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, DURATION_S);
    o1.connect(g);
    o2.connect(g);
    g.connect(offline.destination);
    o1.start(0);
    o2.start(0);
    o1.stop(DURATION_S);
    o2.stop(DURATION_S);
    const buffer = await offline.startRendering();
    const blob = audioBufferToWavBlob(buffer);
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.preload = 'auto';
    return audio;
  } catch {
    return null;
  }
};

const ensureChimeAudio = (): Promise<HTMLAudioElement | null> => {
  if (cachedAudio) return Promise.resolve(cachedAudio);
  if (renderInFlight) return renderInFlight;
  renderInFlight = renderChimeAudio().then((audio) => {
    cachedAudio = audio;
    renderInFlight = null;
    return audio;
  });
  return renderInFlight;
};

// Pre-warm so the first chime in a session doesn't pay the offline-render cost.
// Safe to call before any user gesture — only renders into a blob, doesn't play.
export const prewarmMessageChime = (): void => {
  void ensureChimeAudio();
};

export const playMessageChime = async (): Promise<void> => {
  if (!getSoundEnabled()) return;
  const audio = await ensureChimeAudio();
  if (!audio) return;
  try {
    // Reset to the start in case the element is mid-playback from a
    // previous fire (busy thread → multiple chimes ~500ms apart).
    audio.currentTime = 0;
    await audio.play();
  } catch {
    // Autoplay policy blocked us — the user hasn't interacted with
    // the page yet. Silent fail; the OS-level browser notification
    // (if opted in) still fires.
  }
};

// --- browser notification ----------------------------------------------------

export interface BrowserNotificationPayload {
  title: string;
  body: string;
  conversationId: string;
  messageId: string;
}

// Shared gating + construction for OS-level pop-ups. Fires only when
// the user opted in, permission is granted, and the tab is NOT the
// thing they're looking at (hidden tab or unfocused window) — i.e.
// exactly the "I was on another tab and missed it" case from the
// issue reports. Used for new messages and for @mentions/tags.
export const fireGenericBrowserNotification = (
  payload: { title: string; body: string; tag: string },
  onClick?: () => void,
): void => {
  if (!isNotificationApiSupported()) return;
  if (!getBrowserNotificationsEnabled()) return;
  // If the OS/browser has revoked permission after we cached the
  // localStorage flag, gracefully roll the pref back so the prefs UI
  // surfaces the truth on next read.
  if (Notification.permission !== 'granted') {
    if (Notification.permission === 'denied') {
      try {
        window.localStorage.removeItem('messaging-prefs-browser-notifications-v1');
      } catch {
        /* ignore */
      }
    }
    return;
  }
  if (typeof document !== 'undefined' && document.visibilityState === 'visible' &&
      document.hasFocus()) {
    return;
  }
  try {
    const n = new Notification(payload.title, {
      body: payload.body,
      icon: '/dashalign_favicon.png',
      badge: '/dashalign_favicon.png',
      tag: payload.tag,
      renotify: false,
    });
    n.onclick = () => {
      window.focus();
      n.close();
      onClick?.();
    };
  } catch {
    /* notification construction may fail in some browsers */
  }
};

export const fireBrowserNotification = (
  payload: BrowserNotificationPayload,
  onClick?: () => void,
): void =>
  fireGenericBrowserNotification(
    { title: payload.title, body: payload.body, tag: `messaging:${payload.conversationId}` },
    onClick,
  );
